/**
 * Debug overlay: wind arrows on a polar grid, an Rmax ring, and a 2D inset
 * plot of V(r). This is the visual check that the Holland profile and the
 * rotation sign are right.
 *
 * Arrows are sampled through WindField.sampleMean: the same code path the
 * tracers use, minus turbulence, so the overlay shows the mean flow the
 * debris feels (including steering and inflow). Arrows are stored relative
 * to the storm center; the group follows the center each frame.
 * Rebuilds only when parameters change.
 */
import {
  Color,
  ConeGeometry,
  DoubleSide,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicNodeMaterial,
  Quaternion,
  RingGeometry,
  Vector3,
} from 'three/webgpu';
import { DOMAIN_RADIUS_IN_RMAX, RENDER_UNITS_PER_M } from '../config/constants';
import { hollandTangentialSpeed } from '../physics/hollandProfile';
import type { WindField, WindSample } from '../physics/windField';

const RINGS = 10;
const SPOKES = 24;
const ARROW_HEIGHT_KM = 0.3;
/** Arrow length at the peak wind speed, km. */
const ARROW_MAX_LEN_KM = 9;

export interface VortexDebugHandle {
  readonly group: Group;
  /** Resample arrows around the storm center at time tS. */
  rebuild(tS: number): void;
  /** Move the overlay to the storm center (km). Allocation-free. */
  setCenter(xKm: number, zKm: number): void;
  setVisible(v: boolean): void;
  dispose(): void;
}

export function createVortexDebug(field: WindField, inset: HTMLCanvasElement): VortexDebugHandle {
  const group = new Group();

  const coneGeo = new ConeGeometry(0.35, 1, 6);
  // Cone points along +y; turn it to point along +x, base at the origin.
  coneGeo.translate(0, 0.5, 0);
  coneGeo.rotateZ(-Math.PI / 2);
  const arrowMat = new MeshBasicNodeMaterial();
  const arrows = new InstancedMesh(coneGeo, arrowMat, RINGS * SPOKES);
  arrows.frustumCulled = false;
  group.add(arrows);

  const ringMat = new MeshBasicNodeMaterial({ color: 0xffd166, side: DoubleSide });
  let ringGeo: RingGeometry | null = null;
  const ring = new Mesh(undefined, ringMat);
  group.add(ring);

  const m = new Matrix4();
  const q = new Quaternion();
  const up = new Vector3(0, 1, 0);
  const pos = new Vector3();
  const scl = new Vector3();
  const tint = new Color();
  const w: WindSample = { x: 0, y: 0, z: 0 };
  const c: WindSample = { x: 0, y: 0, z: 0 };

  function peakSpeed(): number {
    const p = field.holland;
    let best = 0;
    const rEnd = DOMAIN_RADIUS_IN_RMAX * p.rMaxM;
    for (let i = 1; i <= 400; i++) {
      const v = hollandTangentialSpeed((rEnd * i) / 400, p);
      if (v > best) best = v;
    }
    return best;
  }

  function drawInset(vPeak: number): void {
    const ctx = inset.getContext('2d');
    if (!ctx) return;
    const p = field.holland;
    // Canvas backing store is 2x its CSS size; draw in CSS pixels.
    const px = inset.width / (inset.clientWidth || inset.width / 2);
    ctx.setTransform(px, 0, 0, px, 0, 0);
    const W = inset.width / px;
    const H = inset.height / px;
    const pad = 28;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(13,17,23,0.85)';
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = '#8b949e';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad, 8);
    ctx.lineTo(pad, H - pad);
    ctx.lineTo(W - 8, H - pad);
    ctx.stroke();

    const rEnd = DOMAIN_RADIUS_IN_RMAX * p.rMaxM;
    const vTop = vPeak > 0 ? vPeak * 1.1 : 1;
    const xOf = (r: number): number => pad + ((W - pad - 8) * r) / rEnd;
    const yOf = (v: number): number => H - pad - ((H - pad - 8) * v) / vTop;

    // Rmax marker.
    ctx.strokeStyle = '#ffd166';
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(xOf(p.rMaxM), 8);
    ctx.lineTo(xOf(p.rMaxM), H - pad);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.strokeStyle = '#58a6ff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 200; i++) {
      const r = (rEnd * i) / 200;
      const v = hollandTangentialSpeed(r, p);
      if (i === 0) ctx.moveTo(xOf(r), yOf(v));
      else ctx.lineTo(xOf(r), yOf(v));
    }
    ctx.stroke();

    ctx.fillStyle = '#c9d1d9';
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillText(`V(r), peak ${vPeak.toFixed(1)} m/s`, pad + 4, 18);
    ctx.fillText('Rmax', xOf(p.rMaxM) + 3, H - pad - 4);
    ctx.fillText(`r (0 to ${(rEnd / 1000).toFixed(0)} km)`, W / 2 - 30, H - 8);
  }

  function rebuild(tS: number): void {
    const p = field.holland;
    field.stormCenter(tS, c);
    const vPeak = peakSpeed();
    const rEnd = DOMAIN_RADIUS_IN_RMAX * p.rMaxM;
    let idx = 0;
    for (let ri = 1; ri <= RINGS; ri++) {
      const rM = (rEnd * ri) / RINGS;
      for (let s = 0; s < SPOKES; s++) {
        const th = (2 * Math.PI * s) / SPOKES;
        const xM = rM * Math.cos(th);
        const zM = rM * Math.sin(th);
        field.sampleMean(c.x + xM, ARROW_HEIGHT_KM / RENDER_UNITS_PER_M, c.z + zM, tS, w);
        const speed = Math.hypot(w.x, w.z);
        // Rotation about +y by angle a maps +x to (cos a, 0, -sin a).
        q.setFromAxisAngle(up, Math.atan2(-w.z, w.x));
        const len = vPeak > 0 ? (ARROW_MAX_LEN_KM * speed) / vPeak : 0;
        pos.set(xM * RENDER_UNITS_PER_M, ARROW_HEIGHT_KM, zM * RENDER_UNITS_PER_M);
        scl.set(Math.max(len, 1e-3), 1, 1);
        m.compose(pos, q, scl);
        arrows.setMatrixAt(idx, m);
        tint.setHSL(0.6 - 0.6 * (vPeak > 0 ? speed / vPeak : 0), 0.85, 0.55);
        arrows.setColorAt(idx, tint);
        idx++;
      }
    }
    arrows.instanceMatrix.needsUpdate = true;
    if (arrows.instanceColor) arrows.instanceColor.needsUpdate = true;

    ringGeo?.dispose();
    const rKm = p.rMaxM * RENDER_UNITS_PER_M;
    ringGeo = new RingGeometry(rKm - 0.25, rKm + 0.25, 128);
    ringGeo.rotateX(-Math.PI / 2);
    ringGeo.translate(0, 0.05, 0);
    ring.geometry = ringGeo;

    drawInset(vPeak);
  }

  rebuild(0);

  return {
    group,
    rebuild,
    setCenter(xKm: number, zKm: number): void {
      group.position.x = xKm;
      group.position.z = zKm;
    },
    setVisible(v: boolean): void {
      group.visible = v;
      inset.hidden = !v;
    },
    dispose(): void {
      coneGeo.dispose();
      arrowMat.dispose();
      ringGeo?.dispose();
      ringMat.dispose();
      arrows.dispose();
    },
  };
}
